import type { Anchors } from "./api.js";
import { generateKeyBetween } from "fractional-indexing";

/** Provisional local keys for a new sequence; the server assigns authoritative keys. */
export function newSequenceKeys(count: number): string[] {
  const keys: string[] = [];
  for (let i = 0; i < count; i++) keys.push(generateKeyBetween(keys.at(-1) ?? null, null));
  return keys;
}

/**
 * Where a dragged row would land, worked out from rectangles alone.
 *
 * Pure, and deliberately so. `src/views/reorder.ts` owns the pointer plumbing —
 * capture, the ghost, the marker, auto-scroll — and none of that can be tested
 * here: jsdom has no layout, so `getBoundingClientRect` returns zeros and
 * `elementFromPoint` returns null. Everything worth getting right is in this
 * function instead, where a test can hand it the geometry directly.
 *
 * The answer is a **gap**, not a row, for the same reason `step` in the list
 * screen works in gaps: a position is the space between two neighbours, and
 * naming the row you are nearest to leaves "above it or below it" still to
 * decide. Gap `n` is the space before `rows[n]`, so a list of `n` rows has
 * `n + 1` gaps.
 */
export interface Placed {
  /** The row's own top and bottom, in the same space as the pointer. */
  id: string;
  top: number;
  bottom: number;
}

/**
 * The gap the pointer is over, with the dragged row taken out of the list
 * first.
 *
 * Taking it out matters: with it left in, dropping a row back where it started
 * names a gap on the wrong side of itself, and the move reads as one step
 * rather than none.
 */
export function gapAt(rows: Placed[], dragged: string, y: number): number {
  const others = rows.filter((row) => row.id !== dragged);
  for (let at = 0; at < others.length; at += 1) {
    const row = others[at]!;
    // The midpoint, so the gap flips as the pointer crosses the middle of a
    // row rather than when it leaves the row entirely — which would leave a
    // dead band between every pair where nothing moves.
    if (y < row.top + (row.bottom - row.top) / 2) return at;
  }
  return others.length;
}

/**
 * The neighbours either side of a gap, which is the only vocabulary the server
 * takes for a position.
 *
 * Both travel. Either can be deleted on another device while the mutation sits
 * in the queue, and the survivor is what keeps the move meaningful rather than
 * rejected — see `Anchors` in `lib/api.ts`.
 */
export function anchorsFor(rows: Placed[], dragged: string, gap: number): Anchors {
  const others = rows.filter((row) => row.id !== dragged);
  return {
    afterId: others[gap - 1]?.id ?? null,
    beforeId: others[gap]?.id ?? null,
  };
}

/**
 * Whether a drop at this gap would actually move the row.
 *
 * A drag that ends where it started must send nothing: a mutation that changes
 * no order still bumps `updated_at`, pushes a row into every other device's
 * delta, and — with undo in the picture — offers to undo something that did not
 * happen.
 */
export function moves(rows: Placed[], dragged: string, gap: number): boolean {
  const at = rows.findIndex((row) => row.id === dragged);
  if (at < 0) return false;
  // With the row lifted out, it currently sits in gap `at`: the rows before it
  // are unchanged, and everything after has shifted down by one.
  return gap !== at;
}

/**
 * One step up or down, as the neighbours it lands between.
 *
 * Shared because the row's handle and the actions menu both offer it and must
 * agree: Move up and Move down in the menu are the explicit alternative to
 * dragging (`docs/product-design-system.md`), so the same arithmetic runs in
 * two places.
 *
 * `undefined` at the ends rather than a no-op mutation — see `moves`.
 */
export function stepAnchors(
  siblings: readonly { id: string }[],
  id: string,
  by: -1 | 1,
): Anchors | undefined {
  return visibleStep(siblings, siblings, id, by);
}

/**
 * One step among the rows that are *drawn*, named against the rows that
 * *exist*.
 *
 * With completed rows hidden (C03) the two differ, and the spec's rule for a
 * move is the drop rule below: down goes after the next visible row, up goes
 * before the previous one, and the other neighbour is that row's actual
 * neighbour in the full list — hidden or not — so hidden rows keep their
 * relative order and never move. With nothing hidden, `visible` is `full`
 * and this is the plain one-step arithmetic `stepAnchors` always was.
 *
 * `undefined` at the visible ends, even when hidden rows lie beyond: a step
 * past the last row on screen would be a move the person cannot see.
 */
export function visibleStep(
  full: readonly { id: string }[],
  visible: readonly { id: string }[],
  id: string,
  by: -1 | 1,
): Anchors | undefined {
  const at = visible.findIndex((row) => row.id === id);
  if (at < 0 || at + by < 0 || at + by >= visible.length) return undefined;
  const target = visible[at + by]!.id;
  const others = full.filter((row) => row.id !== id);
  const there = others.findIndex((row) => row.id === target);
  if (there < 0) return undefined;
  return by > 0
    ? { afterId: target, beforeId: others[there + 1]?.id ?? null }
    : { afterId: others[there - 1]?.id ?? null, beforeId: target };
}

/**
 * The pair a drop lands between when only some rows are drawn.
 *
 * A drag sees gaps between the rows on screen, and a gap between two visible
 * rows may hold hidden ones. The spec settles which side the drop takes:
 * **before the visible row below it**, with that row's actual predecessor —
 * hidden or not — as the other neighbour. In A, completed B, C, dropping D
 * just above C gives A, B, D, C. At the bottom, where no visible row is
 * below, the drop goes after the true last row of the list, so a row dragged
 * to the end lands at the end and not above the hidden tail.
 *
 * The dragged row is taken out of `full` first, as `anchorsFor` does: a drop
 * just below itself must not name itself as a neighbour.
 */
export function visibleAnchors(
  full: readonly { id: string }[],
  dragged: string,
  before: string | null,
): Anchors {
  const others = full.filter((row) => row.id !== dragged);
  if (before === null) return { afterId: others[others.length - 1]?.id ?? null, beforeId: null };
  const at = others.findIndex((row) => row.id === before);
  if (at < 0) return { afterId: null, beforeId: null };
  return { afterId: others[at - 1]?.id ?? null, beforeId: before };
}

/**
 * Holds a drop inside the dragged row's category while a list is ordered by
 * category (g81).
 *
 * `allowed` is every row of that category. The drawn ones stand together, so
 * the gaps a drop may take run from just above the first of them to just
 * below the last; a pointer past either end is held at that end, and the
 * marker with it. A category with no other row drawn has nowhere to go, so
 * the gap is the one the row already sits in, which `moves` reads as no move.
 */
export function clampGap(rows: Placed[], dragged: string, gap: number, allowed: ReadonlySet<string>): number {
  const others = rows.filter((row) => row.id !== dragged);
  let first = -1;
  let last = -1;
  others.forEach((row, at) => {
    if (!allowed.has(row.id)) return;
    if (first < 0) first = at;
    last = at;
  });
  if (first < 0) return rows.findIndex((row) => row.id === dragged);
  return Math.min(Math.max(gap, first), last + 1);
}

/**
 * The pair a drop lands between when it is held inside a category (g81).
 *
 * `full` is the list in its normal order and `group` the category's rows.
 * Above a drawn row of the category the drop takes `visibleAnchors`: before
 * that row, with its real predecessor in the normal order as the other
 * neighbour. So the row's normal position sits directly beside the row it was
 * dropped next to, and the list shows it there when the ordering is switched
 * off. The predecessor may belong to another category, and is never the row
 * drawn above the gap, which the normal order may hold far away.
 *
 * At the bottom of the category the row below the gap is another category's,
 * or none: the drop goes after the category's last row in the normal order,
 * hidden ones included, as a drop at the end of a list goes after its true
 * last row. With every row in the group this is `visibleAnchors` exactly.
 */
export function groupedAnchors(
  full: readonly { id: string }[],
  group: ReadonlySet<string>,
  dragged: string,
  before: string | null,
): Anchors {
  if (before !== null && group.has(before)) return visibleAnchors(full, dragged, before);
  const others = full.filter((row) => row.id !== dragged);
  let last = -1;
  others.forEach((row, at) => { if (group.has(row.id)) last = at; });
  if (last < 0) return visibleAnchors(full, dragged, before);
  return { afterId: others[last]!.id, beforeId: others[last + 1]?.id ?? null };
}
